import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { SHIPMENT_STAGES } from '#/entities/shipment'
import type { ShipmentStage } from '#/entities/shipment'
import type { ShipmentListItem } from '#/shared/api/generated/shipment/models'
import { useListShipmentsApiV1ShipmentsGet } from '#/shared/api/generated/shipment/theCargoShipment'

import { mapActiveLoads } from '../lib/map-active-loads'
import type { ActiveLoad } from './conversation'

/** Upper bound fetched in one call (API caps size at 100); the strip collapses
 *  3+ behind a toggle. */
const MAX_LOADS = 60

/** Strip order: orders first (most actionable), then quotes, then leads. */
const STAGE_ORDER = [
  SHIPMENT_STAGES.ORDER,
  SHIPMENT_STAGES.QUOTE,
  SHIPMENT_STAGES.LEAD,
] as const

/**
 * Partition a customer's shipments by stage, read from each item's `stage`
 * field. (`stage` is returned by the API but not yet in the generated
 * ShipmentListItem type — read it through a cast until the schema is regenerated.)
 */
function groupByStage(
  items: ReadonlyArray<ShipmentListItem>,
): Record<ShipmentStage, ShipmentListItem[]> {
  const byStage: Record<ShipmentStage, ShipmentListItem[]> = {
    [SHIPMENT_STAGES.ORDER]: [],
    [SHIPMENT_STAGES.QUOTE]: [],
    [SHIPMENT_STAGES.LEAD]: [],
  }
  for (const item of items) {
    const stage = (item as { stage?: ShipmentStage }).stage
    const bucket = stage ? byStage[stage] : undefined
    if (bucket) bucket.push(item)
  }
  return byStage
}

export interface ActiveLoadsState {
  /** Loads partitioned and ordered by stage; empty until resolved. */
  loads: ActiveLoad[]
  /** The list call is in flight (only ever true once a customer is known). */
  isLoading: boolean
  /** A CRM customer is linked, so a loads request was made. */
  hasCustomer: boolean
}

/**
 * The counterparty's loads for the conversation header strip. A single list call
 * (no stage filter) returns all the customer's shipments; we partition them by
 * stage client-side so each row carries its stage and opens the right detail
 * route. Resolved from their CRM customer id; idle until a customer is known.
 *
 * Exposes `isLoading`/`hasCustomer` so the strip can show a skeleton while the
 * call is in flight and an empty state when a known customer has no loads.
 */
export function useActiveLoads(
  customerId: string | undefined,
): ActiveLoadsState {
  const { t } = useTranslation()
  const query = useListShipmentsApiV1ShipmentsGet(
    { customer_id: customerId, size: MAX_LOADS },
    { query: { enabled: !!customerId } },
  )
  const items = query.data?.data.items

  const loads = useMemo(() => {
    const byStage = groupByStage(items ?? [])
    return STAGE_ORDER.flatMap((stage) =>
      mapActiveLoads(byStage[stage], stage, t),
    )
  }, [items, t])

  return { loads, isLoading: query.isLoading, hasCustomer: !!customerId }
}
